import { InferType } from 'yup'
import { Form, Formik, type FormikProps, type FormikTouched } from 'formik'
import FormikTextInput from '@/shared/forms/components/FormikTextInput'
import React, { useState } from 'react'
import { FormProps } from '@/shared/components/hi/types'
import { MedicareFormValidator } from '@/shared/components/hi/MedicareFormValidator'

import medicareCardImage from '../../../public/images/medicare-card.png'
import medicareCardNumberImage from '../../../public/images/medicare-card-number.png'
import medicareCardIrnImage from '../../../public/images/medicare-card-irn.png'
import medicareCardExpiryImage from '../../../public/images/medicare-card-expiry.png'
import Image from 'next/image'
import clsx from 'clsx'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'

export interface MedicareFormType extends InferType<
  typeof MedicareFormValidator
> {}

type SelectedField = 'none' | 'number' | 'irn' | 'expiry'

type Props = FormProps<MedicareFormType> & {
  variant?: 'default' | 'quiz' | 'profile'
  duplicateAccountError?: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
  linkedIhi?: string
}

export function MedicareForm({
  onVerify,
  onInputChanged,
  state,
  children,
  variant = 'default',
  duplicateAccountError = false,
  onClearServerError,
  onClickLogin,
  linkedIhi,
}: Props) {
  const [selectedField, setSelectedField] = useState<SelectedField>('none')

  return (
    <Formik
      initialValues={state.values}
      initialTouched={state.touched}
      onSubmit={async (values, { setSubmitting }) => {
        await onVerify(values)
        setSubmitting(false)
      }}
      validationSchema={MedicareFormValidator}
      validateOnMount={true}
    >
      {(props) => {
        if (variant === 'quiz') {
          return (
            <QuizMedicareForm
              duplicateAccountError={duplicateAccountError}
              formik={props}
              onClearServerError={onClearServerError}
              onClickLogin={onClickLogin}
              onInputChanged={onInputChanged}
            >
              {children?.(props)}
            </QuizMedicareForm>
          )
        }

        if (variant === 'profile') {
          return (
            <ProfileMedicareForm
              formik={props}
              linkedIhi={linkedIhi}
              onInputChanged={onInputChanged}
              state={state}
            >
              {children?.(props)}
            </ProfileMedicareForm>
          )
        }

        const { values, handleChange, touched, handleBlur } = props

        return (
          <Form>
            <div className="mx-auto h-auto w-full py-8 sm:px-12">
              <Image
                src={medicareCardImage}
                alt="Medicare Card with no fields highlighted"
                className={clsx(
                  'h-full w-full',
                  selectedField === 'none' ? '' : 'hidden',
                )}
                placeholder="blur"
                priority
              />
              <Image
                src={medicareCardNumberImage}
                alt="Medicare Card with number field highlighted"
                className={clsx(
                  'h-full w-full',
                  selectedField === 'number' ? '' : 'hidden',
                )}
                placeholder="blur"
                priority
              />
              <Image
                src={medicareCardIrnImage}
                alt="Medicare Card with IRN field highlighted"
                className={clsx(
                  'h-full w-full',
                  selectedField === 'irn' ? '' : 'hidden',
                )}
                placeholder="blur"
                priority
              />
              <Image
                src={medicareCardExpiryImage}
                alt="Medicare Card with expiry field highlighted"
                className={clsx(
                  'h-full w-full',
                  selectedField === 'expiry' ? '' : 'hidden',
                )}
                placeholder="blur"
                priority
              />
            </div>

            <div className="col-span-1 grid grid-cols-1 gap-x-6 gap-y-6 sm:col-span-6 sm:grid-cols-6">
              <FormikTextInput
                name="number"
                label="Medicare Number"
                autocomplete="medicare-number"
                size="full"
                placeholder="8724302101"
                onChange={(
                  ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleChange(ev)
                  onInputChanged(
                    { ...values, number: ev.target.value },
                    { ...touched, number: true },
                  )
                }}
                onBlur={(
                  ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleBlur(ev)
                  onInputChanged(values, { ...touched, number: true })
                  setSelectedField('none')
                }}
                onFocus={() => setSelectedField('number')}
              />
              <FormikTextInput
                name="irn"
                label="IRN (This is the number in front of your name)"
                autocomplete="medicare-irn"
                size="1/2"
                placeholder="1"
                type="number"
                inputMode="numeric"
                onChange={(
                  ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleChange(ev)
                  const irn = Number(ev.target.value)
                  const val = Number.isNaN(irn)
                    ? { ...values }
                    : { ...values, irn }
                  onInputChanged(val, { ...touched, irn: true })
                }}
                onBlur={(
                  ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleBlur(ev)
                  onInputChanged(values, { ...touched, irn: true })
                  setSelectedField('none')
                }}
                onFocus={() => setSelectedField('irn')}
              />
              <FormikTextInput
                name="expiry"
                label="Expiry"
                autocomplete="medicare-expiry"
                size="1/2"
                placeholder="MM/YYYY"
                onChange={(
                  ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleChange(ev)
                  onInputChanged(
                    { ...values, expiry: ev.target.value },
                    { ...touched, expiry: true },
                  )
                }}
                onBlur={(
                  ev: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => {
                  handleBlur(ev)
                  onInputChanged(values, { ...touched, expiry: true })
                  setSelectedField('none')
                }}
                onFocus={() => setSelectedField('expiry')}
              />
            </div>
            {children?.(props)}
          </Form>
        )
      }}
    </Formik>
  )
}

function ProfileMedicareForm({
  children,
  formik,
  linkedIhi,
  onInputChanged,
  state,
}: {
  children?: React.ReactNode
  formik: FormikProps<MedicareFormType>
  linkedIhi?: string
  onInputChanged: FormProps<MedicareFormType>['onInputChanged']
  state: FormProps<MedicareFormType>['state']
}) {
  const { errors, isSubmitting, touched, values } = formik

  function updateValue(
    name: keyof MedicareFormType,
    value: string | number | undefined,
  ) {
    void formik.setFieldValue(name, value)
    onInputChanged({ ...values, [name]: value } as MedicareFormType, {
      ...touched,
      [name]: true,
    })
  }

  function blurField(name: keyof MedicareFormType) {
    void formik.setFieldTouched(name, true)
    onInputChanged(values, { ...touched, [name]: true })
  }

  return (
    <Form>
      <div className="flex flex-col gap-4">
        <QuizInput
          autoComplete="medicare-number"
          disabled={isSubmitting}
          error={
            touched.number && errors.number ? String(errors.number) : undefined
          }
          label="Medicare number"
          name="number"
          onBlur={() => blurField('number')}
          onChange={(value) => updateValue('number', value)}
          placeholder="10 digits, across the middle of the card"
          value={values.number ?? ''}
        />

        <div className="grid grid-cols-[1fr_1.9fr] gap-[10px] sm:grid-cols-[200px_1fr]">
          <QuizInput
            autoComplete="medicare-irn"
            disabled={isSubmitting}
            error={touched.irn && errors.irn ? String(errors.irn) : undefined}
            label="IRN"
            name="irn"
            onBlur={() => blurField('irn')}
            onChange={(value) => {
              const irn = value === '' ? undefined : Number(value)
              updateValue(
                'irn',
                irn === undefined || Number.isNaN(irn) ? undefined : irn,
              )
            }}
            placeholder="1"
            type="number"
            value={values.irn ?? ''}
          />
          <QuizInput
            autoComplete="medicare-expiry"
            disabled={isSubmitting}
            error={
              touched.expiry && errors.expiry
                ? String(errors.expiry)
                : undefined
            }
            label="Expiry"
            name="expiry"
            onBlur={() => blurField('expiry')}
            onChange={(value) => updateValue('expiry', value)}
            placeholder="MM/YYYY"
            value={values.expiry ?? ''}
          />
        </div>
      </div>

      {state.verified && (
        <div className="mt-4 flex flex-col gap-1">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold leading-[18px] text-[#1F8A70]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#1F8A70]" />
            Verified
          </div>
          {linkedIhi && (
            <p className="pl-[14px] text-[13.5px] leading-[18px] text-[#A0A0A8]">
              ↳ Linked to IHI {formatIdentifier(linkedIhi)}
            </p>
          )}
        </div>
      )}

      {children}
    </Form>
  )
}

function formatIdentifier(value: string) {
  return value
    .replace(/\s/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

function QuizMedicareForm({
  formik,
  onInputChanged,
  duplicateAccountError,
  onClearServerError,
  onClickLogin,
  children,
}: {
  formik: FormikProps<MedicareFormType>
  onInputChanged: (
    values: MedicareFormType,
    touched: FormikTouched<MedicareFormType>,
  ) => void
  duplicateAccountError: boolean
  onClearServerError?: () => void
  onClickLogin?: () => void
  children?: React.ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const { values, touched, errors, isSubmitting } = formik

  function updateValue(
    name: keyof MedicareFormType,
    value: string | number | undefined,
  ) {
    onClearServerError?.()
    void formik.setFieldValue(name, value)
    onInputChanged({ ...values, [name]: value } as MedicareFormType, {
      ...touched,
      [name]: true,
    })
  }

  function blurField(name: keyof MedicareFormType) {
    void formik.setFieldTouched(name, true)
    onInputChanged(values, { ...touched, [name]: true })
  }

  return (
    <Form>
      <motion.div
        animate="visible"
        className="mt-3.5 flex flex-col gap-3.5 sm:mt-[22px] sm:gap-4"
        initial={reduceMotion ? false : 'hidden'}
        variants={{
          hidden: {},
          visible: {
            transition: { delayChildren: 0.04, staggerChildren: 0.065 },
          },
        }}
      >
        <QuizRevealItem reduceMotion={reduceMotion}>
          <div className="flex flex-col items-start gap-3.5 sm:flex-row sm:items-center sm:gap-6">
            <MedicareCardExample />
            <div className="flex max-w-[300px] flex-col gap-2 text-[#5C5C64] max-sm:max-w-[330px]">
              <h2 className="hidden text-[14.5px] font-semibold leading-5 text-[#0B0B0F] sm:block">
                Where to find each number
              </h2>
              <p className="text-[13px] leading-[19px] sm:text-[13.5px] sm:leading-5">
                Your Medicare number is the 10 digits across the middle of the
                card. The circled number before your name is your IRN.
              </p>
            </div>
          </div>
        </QuizRevealItem>

        <QuizRevealItem reduceMotion={reduceMotion}>
          <QuizInput
            autoComplete="medicare-number"
            disabled={isSubmitting}
            error={
              touched.number && errors.number
                ? String(errors.number)
                : undefined
            }
            hasServerError={duplicateAccountError}
            label="Medicare number"
            name="number"
            onBlur={() => blurField('number')}
            onChange={(value) => updateValue('number', value)}
            placeholder="10 digits, across the middle of the card"
            value={values.number ?? ''}
          />
          <DuplicateAccountNotice
            identifierLabel="Medicare number"
            show={duplicateAccountError}
            onClickLogin={onClickLogin}
          />
        </QuizRevealItem>

        <QuizRevealItem reduceMotion={reduceMotion}>
          <div className="grid grid-cols-2 items-start gap-3 sm:gap-4">
            <div className="flex min-w-0 flex-col">
              <QuizInput
                autoComplete="medicare-irn"
                disabled={isSubmitting}
                error={
                  touched.irn && errors.irn ? String(errors.irn) : undefined
                }
                label="IRN"
                name="irn"
                onBlur={() => blurField('irn')}
                onChange={(value) => {
                  const parsed = value === '' ? undefined : Number(value)
                  updateValue('irn', Number.isNaN(parsed) ? undefined : parsed)
                }}
                placeholder="1"
                type="number"
                value={values.irn ?? ''}
              />
              <p className="mt-2 text-[12.5px] leading-[17px] text-[#82828B] sm:text-[13px] sm:leading-[18px]">
                The IRN is the number before your name.
              </p>
            </div>
            <QuizInput
              autoComplete="medicare-expiry"
              disabled={isSubmitting}
              error={
                touched.expiry && errors.expiry
                  ? String(errors.expiry)
                  : undefined
              }
              label="Expiry"
              name="expiry"
              onBlur={() => blurField('expiry')}
              onChange={(value) => updateValue('expiry', value)}
              placeholder="MM / YYYY"
              value={values.expiry ?? ''}
            />
          </div>
        </QuizRevealItem>
      </motion.div>

      {children}
    </Form>
  )
}

function QuizRevealItem({
  children,
  reduceMotion,
}: {
  children: React.ReactNode
  reduceMotion: boolean | null
}) {
  return (
    <motion.div
      variants={
        reduceMotion
          ? undefined
          : {
              hidden: { opacity: 0, y: 10 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: softMotionDuration.panel,
                  ease: softEditorialEase,
                },
              },
            }
      }
    >
      {children}
    </motion.div>
  )
}

function MedicareCardExample() {
  return (
    <div className="flex h-[200px] w-full shrink-0 flex-col justify-between rounded-2xl bg-[linear-gradient(135deg,#E2F1EC_0%,#CDE7DD_100%)] px-5 py-[18px] sm:h-[196px] sm:w-[320px]">
      <div className="flex justify-end text-[16px] font-bold leading-[21px] tracking-[-0.01em] text-[#17614F] sm:text-[17px] sm:leading-[22px]">
        medicare
      </div>
      <div className="flex flex-col gap-[9px]">
        <div className="text-[20px] font-semibold leading-[25px] tracking-[0.06em] text-[#0B2E25] sm:text-[21px] sm:leading-[26px]">
          2950 61234 1
        </div>
        <div className="flex items-center gap-2">
          <span className="flex h-[23px] w-[23px] items-center justify-center rounded-full border-[1.5px] border-[#17614F] text-[12.5px] font-bold leading-[15px] text-[#17614F] sm:h-6 sm:w-6 sm:text-[13px] sm:leading-4">
            1
          </span>
          <span className="text-[13.5px] font-medium leading-[17px] tracking-[0.04em] text-[#0B2E25] sm:text-sm sm:leading-[18px]">
            SARAH CITIZEN
          </span>
        </div>
      </div>
      <div className="flex justify-end text-[10.5px] font-medium leading-[13px] tracking-[0.05em] text-[#3E7A67] sm:text-[11px] sm:leading-[14px]">
        VALID TO 08/2028
      </div>
    </div>
  )
}

type QuizInputProps = {
  autoComplete: string
  disabled: boolean
  error?: string
  hasServerError?: boolean
  label: string
  name: string
  onBlur: () => void
  onChange: (value: string) => void
  placeholder: string
  type?: React.HTMLInputTypeAttribute
  value: string | number
}

export function QuizInput({
  autoComplete,
  disabled,
  error,
  hasServerError,
  label,
  name,
  onBlur,
  onChange,
  placeholder,
  type = 'text',
  value,
}: QuizInputProps) {
  const hasError = Boolean(error || hasServerError)

  return (
    <div className="flex min-w-0 flex-col gap-[7px] sm:gap-2">
      <label
        className="text-[13px] font-semibold leading-[17px] sm:text-[13.5px] sm:leading-[18px]"
        htmlFor={name}
      >
        {label}
      </label>
      <input
        autoComplete={autoComplete}
        className={clsx(
          'h-[50px] w-full rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 text-[14.5px] leading-[19px] text-[#0B0B0F] outline-none transition-[border-color,background-color] duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F]/45 disabled:cursor-not-allowed disabled:opacity-60 sm:h-[52px] sm:px-[18px] sm:text-[15px] sm:leading-5',
          hasError && 'border-[#C6453C] focus:border-[#C6453C]',
        )}
        disabled={disabled}
        id={name}
        inputMode={type === 'number' ? 'numeric' : 'text'}
        name={name}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
      {error && !hasServerError && <QuizErrorMessage message={error} />}
    </div>
  )
}

export function DuplicateAccountNotice({
  identifierLabel,
  show,
  onClickLogin,
}: {
  identifierLabel: string
  show: boolean
  onClickLogin?: () => void
}) {
  const reduceMotion = useReducedMotion()

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          animate={{ height: 'auto', opacity: 1 }}
          className="overflow-hidden"
          exit={reduceMotion ? undefined : { height: 0, opacity: 0, y: -4 }}
          initial={reduceMotion ? false : { height: 0, opacity: 0, y: -5 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  duration: softMotionDuration.panel,
                  ease: softEditorialEase,
                }
          }
        >
          <div className="mt-2.5 flex flex-col gap-2 rounded-[14px] bg-[#FAEDEB] px-3.5 py-3 sm:flex-row sm:items-center sm:gap-3 sm:px-4 sm:py-3.5">
            <div className="flex min-w-0 items-start gap-2 text-[#C6453C]">
              <ErrorIcon />
              <p className="text-[13px] font-normal leading-[18px] sm:text-[13.5px] sm:leading-[19px]">
                This {identifierLabel} is already linked to a NextClinic
                account.
              </p>
            </div>
            {onClickLogin && (
              <button
                className="ml-[22px] w-fit shrink-0 rounded-full bg-[#0B0B0F] px-4 py-2 text-[12.5px] font-semibold leading-[15px] text-white transition-colors duration-300 hover:bg-[#242428] sm:ml-auto"
                onClick={onClickLogin}
                type="button"
              >
                Sign in
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function QuizErrorMessage({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 text-[#C6453C]">
      <ErrorIcon />
      <p className="text-[13px] font-normal leading-[18px] sm:text-[13.5px] sm:leading-[19px]">
        {message}
      </p>
    </div>
  )
}

function ErrorIcon() {
  return (
    <svg
      aria-hidden="true"
      className="mt-0.5 h-3.5 w-3.5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <path
        d="M12 8v5M12 16h.01"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}
